import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR, formatDateTimeBR, STATUS_LABELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatusBadge } from "../components/Badges";
import { toast } from "sonner";
import {
  Wallet, Check, Calendar, Filter, Loader2, History, ChevronDown, ChevronUp,
  MessageCircle, Undo2,
} from "lucide-react";
import InstallmentCommissionsSection from "../components/InstallmentCommissionsSection";

const PERIODS = [
  { v: "", label: "Tudo" },
  { v: "today", label: "Hoje" },
  { v: "week", label: "Semana" },
  { v: "month", label: "Mês" },
  { v: "custom", label: "Personalizado" },
];

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="fc-card p-5 fc-card-hover" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] font-medium">{label}</div>
      <div className={`fc-kpi-value text-2xl sm:text-[26px] mt-2 leading-none ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function CommissionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [period, setPeriod] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [report, setReport] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openInd, setOpenInd] = useState({});
  const [selected, setSelected] = useState({}); // { indicator_id: { client_id: true } }
  const [payNotes, setPayNotes] = useState({});
  const [paying, setPaying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (period) params.period = period;
      if (period === "custom") {
        if (start) params.start = start;
        if (end) params.end = end;
      }
      const [r1, r2] = await Promise.all([
        api.get("/commissions/report", { params }),
        api.get("/commissions/payments"),
      ]);
      setReport(r1.data);
      setPayments(r2.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao carregar comissões");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (period !== "custom" || (start && end)) {
      const t = setTimeout(load, 200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [period, start, end]);

  const totals = useMemo(() => {
    if (!report?.indicators) return { total: 0, paid: 0, pending: 0, clients: 0, approved: 0 };
    return report.indicators.reduce((acc, g) => ({
      total: acc.total + g.total_commission,
      paid: acc.paid + g.paid_commission,
      pending: acc.pending + g.pending_commission,
      clients: acc.clients + g.clients_count,
      approved: acc.approved + g.total_approved,
    }), { total: 0, paid: 0, pending: 0, clients: 0, approved: 0 });
  }, [report]);

  const toggle = (iid) => setOpenInd(prev => ({ ...prev, [iid]: !prev[iid] }));

  const toggleClient = (iid, cid) => {
    setSelected(prev => {
      const cur = { ...(prev[iid] || {}) };
      if (cur[cid]) delete cur[cid]; else cur[cid] = true;
      return { ...prev, [iid]: cur };
    });
  };

  const payAll = async (group, onlyPending = true) => {
    const sel = selected[group.indicator_id] || {};
    let ids = Object.keys(sel);
    if (ids.length === 0) {
      ids = group.clients.filter(c => onlyPending ? !c.commission_paid : true).map(c => c.id);
    }
    if (ids.length === 0) {
      toast.error("Nada para pagar");
      return;
    }
    if (!window.confirm(`Marcar ${ids.length} comissão(ões) como paga(s) para ${group.indicator_name}?`)) return;
    setPaying(group.indicator_id);
    try {
      await api.post("/commissions/pay", {
        indicator_id: group.indicator_id,
        client_ids: ids,
        notes: payNotes[group.indicator_id] || "",
      });
      toast.success("Pagamento registrado");
      setSelected(prev => ({ ...prev, [group.indicator_id]: {} }));
      setPayNotes(prev => ({ ...prev, [group.indicator_id]: "" }));
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao pagar");
    } finally { setPaying(null); }
  };

  const revert = async (pid) => {
    if (!window.confirm("Reverter este pagamento? As comissões voltarão para pendente.")) return;
    try {
      await api.delete(`/commissions/payments/${pid}`);
      toast.success("Pagamento revertido");
      load();
    } catch (_) { toast.error("Erro ao reverter"); }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Financeiro</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Comissões</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">
            {isAdmin ? "Relatório completo por indicador, com filtros e pagamentos." : "Acompanhe suas comissões e pagamentos."}
          </p>
        </div>
      </div>

      {/* Period filters */}
      <div className="fc-card p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap" data-testid="period-filters">
        <div className="flex items-center gap-2 text-[#A3A3A3] text-sm">
          <Filter size={14}/> Período:
        </div>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map(p => (
            <button
              key={p.v}
              onClick={() => setPeriod(p.v)}
              className={`px-3.5 py-1.5 rounded-md text-[12.5px] border transition-colors ${period === p.v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#262626] text-[#A3A3A3] hover:border-[#3a3a3a]"}`}
              data-testid={`period-${p.v || "all"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2 ml-auto">
            <input type="date" className="fc-input text-sm" value={start} onChange={e=>setStart(e.target.value)} data-testid="period-start"/>
            <span className="text-[#737373]">→</span>
            <input type="date" className="fc-input text-sm" value={end} onChange={e=>setEnd(e.target.value)} data-testid="period-end"/>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <Kpi label={isAdmin ? "Comissão total" : "Minha comissão total"} value={formatBRL(totals.total)} accent="text-[#D4AF37]" testid="kpi-total"/>
        <Kpi label="Comissão paga" value={formatBRL(totals.paid)} accent="text-emerald-300" testid="kpi-paid"/>
        <Kpi label="Pendente" value={formatBRL(totals.pending)} accent="text-yellow-300" testid="kpi-pending"/>
        <Kpi label={isAdmin ? "Valor aprovado" : "Clientes aprovados"} value={isAdmin ? formatBRL(totals.approved) : totals.clients} testid="kpi-extra"/>
      </div>

      {/* Indicator groups */}
      <div className="space-y-4">
        {loading && [1,2].map(i => <div key={i} className="fc-card h-32 shimmer"/>)}
        {!loading && (report?.indicators || []).length === 0 && (
          <div className="fc-card p-10 text-center text-[#737373]">Nenhuma comissão no período selecionado.</div>
        )}
        {!loading && (report?.indicators || []).map(g => {
          const isOpen = !!openInd[g.indicator_id];
          const sel = selected[g.indicator_id] || {};
          const selCount = Object.keys(sel).length;
          const pendingCount = g.clients.filter(c => !c.commission_paid).length;
          return (
            <div key={g.indicator_id} className="fc-card overflow-hidden" data-testid={`commission-group-${g.indicator_id}`}>
              <button onClick={() => toggle(g.indicator_id)} className="w-full flex items-center justify-between p-5 hover:bg-[#0f0f0f] transition-colors text-left">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] font-display font-bold">
                    {g.indicator_name?.charAt(0) || "?"}
                  </div>
                  <div>
                    <div className="font-display text-[16px] font-semibold tracking-tight">{g.indicator_name}</div>
                    <div className="text-[12px] text-[#737373]">{g.clients_count} cliente(s) • {g.commission_percent}% • {pendingCount} pendente(s)</div>
                  </div>
                </div>
                <div className="flex items-center gap-5">
                  <div className="hidden sm:block text-right">
                    <div className="text-[10px] uppercase tracking-wider text-[#737373]">Total</div>
                    <div className="font-mono-tab text-[#D4AF37] font-semibold">{formatBRL(g.total_commission)}</div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-[10px] uppercase tracking-wider text-[#737373]">Pendente</div>
                    <div className="font-mono-tab text-yellow-300 font-semibold">{formatBRL(g.pending_commission)}</div>
                  </div>
                  {isOpen ? <ChevronUp size={18} className="text-[#737373]"/> : <ChevronDown size={18} className="text-[#737373]"/>}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-[#1f1f1f] p-5 space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm sm:hidden">
                    <div><div className="text-[10px] uppercase text-[#737373]">Aprovado</div><div className="font-mono-tab">{formatBRL(g.total_approved)}</div></div>
                    <div><div className="text-[10px] uppercase text-[#737373]">Total</div><div className="font-mono-tab text-[#D4AF37]">{formatBRL(g.total_commission)}</div></div>
                    <div><div className="text-[10px] uppercase text-[#737373]">Pago</div><div className="font-mono-tab text-emerald-300">{formatBRL(g.paid_commission)}</div></div>
                    <div><div className="text-[10px] uppercase text-[#737373]">Pendente</div><div className="font-mono-tab text-yellow-300">{formatBRL(g.pending_commission)}</div></div>
                  </div>

                  <div className="overflow-x-auto -mx-5 px-5">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                          {isAdmin && <th className="py-2 px-2 w-8"></th>}
                          <th className="py-2 px-2 text-left">Cliente</th>
                          <th className="py-2 px-2 text-left">Aprovado</th>
                          <th className="py-2 px-2 text-left">Comissão</th>
                          <th className="py-2 px-2 text-left">Status</th>
                          <th className="py-2 px-2 text-left">Pagamento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.clients.map(c => (
                          <tr key={c.id} className="border-b border-[#1a1a1a]">
                            {isAdmin && (
                              <td className="py-2.5 px-2">
                                {!c.commission_paid && (
                                  <input
                                    type="checkbox"
                                    checked={!!sel[c.id]}
                                    onChange={()=>toggleClient(g.indicator_id, c.id)}
                                    className="accent-[#D4AF37]"
                                    data-testid={`select-client-${c.id}`}
                                  />
                                )}
                              </td>
                            )}
                            <td className="py-2.5 px-2">{c.full_name}</td>
                            <td className="py-2.5 px-2 font-mono-tab">{formatBRL(c.approved_amount)}</td>
                            <td className="py-2.5 px-2 font-mono-tab text-[#D4AF37]">{formatBRL(c.commission_amount)}</td>
                            <td className="py-2.5 px-2"><StatusBadge status={c.status}/></td>
                            <td className="py-2.5 px-2">
                              {c.commission_paid ? (
                                <span className="badge-base badge-aprovado"><Check size={11}/> Pago em {formatDateBR(c.commission_paid_at)}</span>
                              ) : (
                                <span className="badge-base badge-em_analise">Pendente</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {isAdmin && pendingCount > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-3 border-t border-[#1f1f1f]">
                      <input
                        className="fc-input text-sm flex-1"
                        placeholder="Observação (opcional)"
                        value={payNotes[g.indicator_id] || ""}
                        onChange={e=>setPayNotes(prev => ({...prev, [g.indicator_id]: e.target.value}))}
                        data-testid={`pay-notes-${g.indicator_id}`}
                      />
                      <button
                        onClick={() => payAll(g)}
                        disabled={paying === g.indicator_id}
                        className="fc-btn-gold flex items-center gap-2 justify-center disabled:opacity-60"
                        data-testid={`pay-button-${g.indicator_id}`}
                      >
                        {paying === g.indicator_id ? <Loader2 size={15} className="animate-spin"/> : <Check size={15}/>}
                        {selCount > 0 ? `Pagar ${selCount} selecionado(s)` : `Pagar todos pendentes (${pendingCount})`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Payments history */}
      <div className="mt-10">
        <h2 className="font-display text-xl font-semibold tracking-tight flex items-center gap-2">
          <History size={18} className="text-[#D4AF37]"/> Histórico de pagamentos
        </h2>
        <p className="text-[#737373] text-[12.5px] mt-1">{payments.length} registro(s)</p>
        <div className="mt-4 space-y-3">
          {payments.length === 0 && (
            <div className="fc-card p-8 text-center text-[#737373]">Nenhum pagamento registrado ainda.</div>
          )}
          {payments.map(p => (
            <div key={p.id} className="fc-card p-4 sm:p-5" data-testid={`payment-${p.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="font-display text-[15px] font-semibold tracking-tight">{p.indicator_name}</div>
                  <div className="text-[12px] text-[#A3A3A3] mt-0.5">
                    {p.clients_count} cliente(s) • {formatDateTimeBR(p.created_at)} • por {p.paid_by_name || p.paid_by_email}
                  </div>
                  {p.notes && <div className="text-[12px] text-[#737373] mt-1.5">📝 {p.notes}</div>}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-[#737373]">Valor pago</div>
                    <div className="font-display text-lg font-bold text-emerald-300">{formatBRL(p.total_amount)}</div>
                  </div>
                  {isAdmin && (
                    <button onClick={()=>revert(p.id)} className="text-red-400/70 hover:text-red-300 p-2 rounded" title="Reverter pagamento" data-testid={`revert-${p.id}`}>
                      <Undo2 size={15}/>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comissões de Parcelados e Acordos (módulo isolado) */}
      <InstallmentCommissionsSection isAdmin={isAdmin} />
    </Layout>
  );
}
